import { useCallback, useEffect, useRef, useState } from 'react';
import type { ImgHTMLAttributes, SyntheticEvent } from 'react';

export type MediaVariant = 'thumb' | 'card' | 'full';

/** Variant widths per spec §10 (WebP, longest-edge fit). */
export const MEDIA_VARIANT_WIDTHS: Record<MediaVariant, number> = {
  thumb: 160,
  card: 480,
  full: 1200,
};

/**
 * Immutable media URL contract (spec §10, layout produced by the WS6
 * variants pipeline): stored keys are `media/original/<rest>`; variants live
 * at `media/{thumb|card|full}/<rest>.webp`.
 *
 * `attempt` > 0 appends a cache-busting parameter. It exists for the retry
 * below and is deliberately absent from the first request, which must stay
 * cacheable — see MEDIA_RETRY_DELAYS_MS.
 */
export function mediaUrl(
  base: string,
  variant: MediaVariant,
  key: string,
  attempt = 0,
): string {
  const rest = key.replace(/^media\/original\//, '');
  const bust = attempt > 0 ? `?retry=${attempt}` : '';
  return `${base.replace(/\/$/, '')}/media/${variant}/${rest}.webp${bust}`;
}

/**
 * Backoff for re-requesting a variant that 404s.
 *
 * The variants are produced by an S3-triggered Lambda (apps/jobs/media-variants),
 * so for a second or two after an upload the key exists and its `.webp`
 * variants do not. The UI renders the new key immediately — correctly, the key
 * is the source of truth — and gets a 404. An `<img>` that has errored never
 * retries on its own, which is why a changed shop picture or profile photo used
 * to stay a broken-image glyph until the app was restarted.
 *
 * Four attempts over ~5.5s comfortably covers a cold-start variant job. Each
 * carries a different `?retry=` value because the media distribution's cache
 * policy includes query strings (`QueryStringBehavior: all`) — without that the
 * retry would just re-read CloudFront's cached 404, which it holds for 10s by
 * default.
 */
export const MEDIA_RETRY_DELAYS_MS = [300, 700, 1500, 3000] as const;

export interface SmartImageProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet' | 'width' | 'height' | 'alt'> {
  /** Media CDN origin, e.g. `https://media.example.com`. */
  base: string;
  /** Immutable media key (new upload = new key). */
  mediaKey: string;
  /** Required — empty string for decorative images. */
  alt: string;
  /** Explicit dimensions — no layout shift (spec §10). */
  width: number;
  height: number;
  /**
   * Largest variant this slot may use. Defaults to `card` — the safe cap for
   * lists/cards (spec §10) — so list consumers can't violate the spec by
   * omission. Detail views and the photo wall must opt in with
   * `maxVariant="full"`; tight thumbnail slots can cap at `thumb`.
   */
  maxVariant?: MediaVariant;
  /** Defaults to the rendered width. */
  sizes?: string;
}

/**
 * The only way UI loads media (spec §10): renders srcset over the generated
 * variants — never originals — with explicit dimensions and lazy loading.
 * Default `maxVariant` is `card`; `full` is opt-in for detail/photo-wall slots.
 */
export function SmartImage({
  base,
  mediaKey,
  alt,
  width,
  height,
  maxVariant = 'card',
  sizes,
  loading = 'lazy',
  decoding = 'async',
  onError,
  ...rest
}: SmartImageProps) {
  const variants: MediaVariant[] = ['thumb', 'card', 'full'];
  const allowed = variants.slice(0, variants.indexOf(maxVariant) + 1);
  const fallback = allowed.includes('card') ? 'card' : allowed[allowed.length - 1] ?? 'thumb';

  // Re-request a freshly uploaded image whose variants are not generated yet.
  const [attempt, setAttempt] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // A new key is a new image: start its retry budget over, or a second upload
  // in the same session would inherit an exhausted one and never load.
  useEffect(() => {
    setAttempt(0);
    return () => clearTimeout(timer.current);
  }, [mediaKey, base]);

  const handleError = useCallback(
    (event: SyntheticEvent<HTMLImageElement, Event>) => {
      onError?.(event);
      const delay = MEDIA_RETRY_DELAYS_MS[attempt];
      // Budget spent: leave the broken image. That is the honest signal that
      // this key has no variants, rather than a request that retries forever.
      if (delay === undefined) return;
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setAttempt(attempt + 1), delay);
    },
    [attempt, onError],
  );

  const srcSet = allowed
    .map((v) => `${mediaUrl(base, v, mediaKey, attempt)} ${MEDIA_VARIANT_WIDTHS[v]}w`)
    .join(', ');

  return (
    <img
      data-testid="ec-smart-image"
      src={mediaUrl(base, fallback, mediaKey, attempt)}
      srcSet={srcSet}
      sizes={sizes ?? `${width}px`}
      alt={alt}
      width={width}
      height={height}
      loading={loading}
      decoding={decoding}
      onError={handleError}
      {...rest}
    />
  );
}
